import { useState, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from '@tanstack/react-table'
import { Activity, Eye, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Archive } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'

interface Booking {
  id: number
  pnr: string
  lastname: string
  monitoring_active: boolean
  has_unread_changes: boolean
  unread_change_count: number
  check_count: number
  change_count: number
  current_state_updated_at: string | null
  last_checked_at: string | null
  monitoring_until: string | null
  is_archived?: boolean
  archived_at?: string | null
}

interface BookingsTableProps {
  bookings: Booking[]
  onRemove: (accessId: number) => void
  baseRoute?: string
}

function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Nie'

  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return 'Gerade eben'
  if (diffMins < 60) return `${diffMins} Min.`
  if (diffHours < 24) return `${diffHours} Std.`
  if (diffDays < 7) return `${diffDays} T.`

  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

const columnHelper = createColumnHelper<Booking>()

export function BookingsTable({ bookings, onRemove, baseRoute = '/lawyer/bookings' }: BookingsTableProps) {
  const navigate = useNavigate()
  const [sorting, setSorting] = useState<SortingState>([])
  const [deleteBooking, setDeleteBooking] = useState<Booking | null>(null)

  const columns = useMemo(
    () => [
      columnHelper.accessor('pnr', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-slate-900 transition-colors"
            onClick={() => column.toggleSorting()}
          >
            PNR
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-3.5 w-3.5" />
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ row }) => (
          <span className="font-mono font-bold text-slate-900 tracking-wide">
            {row.original.pnr}
          </span>
        ),
      }),
      columnHelper.accessor('lastname', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-slate-900 transition-colors"
            onClick={() => column.toggleSorting()}
          >
            Nachname
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-3.5 w-3.5" />
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ getValue }) => (
          <span className="text-slate-700">{getValue()}</span>
        ),
      }),
      columnHelper.accessor('monitoring_active', {
        header: 'Status',
        cell: ({ row }) => {
          const isArchived = row.original.is_archived
          const isMonitoring = row.original.monitoring_active

          if (isArchived) {
            return (
              <Badge className="bg-slate-100 text-slate-600 border-slate-200 font-medium">
                <Archive className="h-3 w-3 mr-1" />
                Archiviert
              </Badge>
            )
          }

          return isMonitoring ? (
            <Badge className="bg-green-100 text-green-700 border-green-200 font-medium">
              <Activity className="h-3 w-3 mr-1" />
              Überwachung
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-slate-100 text-slate-500 font-medium">
              <Eye className="h-3 w-3 mr-1" />
              Manuell
            </Badge>
          )
        },
        enableSorting: false,
      }),
      columnHelper.accessor(
        (row) => row.current_state_updated_at || row.last_checked_at,
        {
          id: 'updated',
          header: ({ column }) => (
            <button
              className="flex items-center gap-1 hover:text-slate-900 transition-colors"
              onClick={() => column.toggleSorting()}
            >
              Aktualisiert
              {column.getIsSorted() === 'asc' ? (
                <ArrowUp className="h-3.5 w-3.5" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowDown className="h-3.5 w-3.5" />
              ) : (
                <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
              )}
            </button>
          ),
          cell: ({ getValue }) => (
            <span className="text-slate-600 text-sm">
              {formatRelativeTime(getValue() as string | null)}
            </span>
          ),
        }
      ),
      columnHelper.accessor('check_count', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-slate-900 transition-colors"
            onClick={() => column.toggleSorting()}
          >
            Prüfungen
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-3.5 w-3.5" />
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ getValue }) => (
          <span className="text-slate-600 tabular-nums">{getValue()}</span>
        ),
      }),
      columnHelper.accessor('change_count', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-slate-900 transition-colors"
            onClick={() => column.toggleSorting()}
          >
            Änderungen
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-3.5 w-3.5" />
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ getValue }) => {
          const count = getValue()
          return (
            <span
              className={cn(
                'tabular-nums font-medium',
                count > 0 ? 'text-orange-600' : 'text-slate-600'
              )}
            >
              {count}
            </span>
          )
        },
      }),
      columnHelper.accessor('has_unread_changes', {
        header: '',
        cell: ({ row }) => {
          const hasUnread = row.original.has_unread_changes
          const unreadCount = row.original.unread_change_count || 0
          if (!hasUnread) return null
          return (
            <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold">
              {unreadCount}
            </span>
          )
        },
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'actions',
        cell: ({ row }) => (
          <button
            onClick={(e) => {
              e.stopPropagation()
              setDeleteBooking(row.original)
            }}
            className={cn(
              'p-1.5 rounded-lg opacity-0 group-hover:opacity-100',
              'bg-slate-100 hover:bg-red-100 text-slate-400 hover:text-red-600',
              'transition-all duration-200',
              'focus:outline-none focus:opacity-100'
            )}
            title="Aus Liste entfernen"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ),
      }),
    ],
    []
  )

  const table = useReactTable({
    data: bookings,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const handleRowClick = (booking: Booking) => {
    navigate({ to: `${baseRoute}/${booking.id}` })
  }

  const handleConfirmDelete = () => {
    if (deleteBooking) {
      onRemove(deleteBooking.id)
      setDeleteBooking(null)
    }
  }

  return (
    <>
      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent border-b-2">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="text-xs uppercase tracking-wider text-slate-500 font-semibold"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-slate-500">
                  Keine Buchungen gefunden.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const hasUnread = row.original.has_unread_changes
                return (
                  <TableRow
                    key={row.id}
                    onClick={() => handleRowClick(row.original)}
                    className={cn(
                      'group cursor-pointer transition-colors',
                      hasUnread
                        ? 'bg-red-50/50 hover:bg-red-50 border-l-4 border-l-red-500'
                        : 'hover:bg-slate-50'
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-3">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteBooking} onOpenChange={() => setDeleteBooking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Buchung aus Liste entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dies entfernt{' '}
              <span className="font-mono font-semibold">{deleteBooking?.pnr}</span> (
              {deleteBooking?.lastname}) aus Ihrer Buchungsliste. Die Buchungsdaten werden nicht
              gelöscht und können später erneut abgerufen werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700">
              Entfernen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
